"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import type { ModuleKey } from "@/lib/modules";
import {
  moduleForPath,
  type ModuleMotionSettings,
  type MotionPreset,
} from "@/lib/motion";

interface MotionRuntimeProps {
  defaultPreset: MotionPreset;
  modulePresets: ModuleMotionSettings;
  homepageModule?: ModuleKey;
}

export function MotionRuntime({
  defaultPreset,
  modulePresets,
  homepageModule,
}: MotionRuntimeProps) {
  const pathname = usePathname();
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(preference.matches);
    updatePreference();
    preference.addEventListener("change", updatePreference);
    return () => preference.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    const main = document.querySelector<HTMLElement>("#main-content");
    if (!main) return;

    const activeModule = moduleForPath(pathname, homepageModule);
    const preset = (activeModule && modulePresets[activeModule]) || defaultPreset;
    const effectivePreset = reducedMotion ? "none" : preset;

    main.dataset.motion = effectivePreset;
    if (activeModule) main.dataset.motionModule = activeModule;
    else delete main.dataset.motionModule;
    main.dataset.motionReady = "true";

    const revealTargets = Array.from(
      main.querySelectorAll<HTMLElement>("[data-motion-reveal], article"),
    );
    revealTargets.forEach((element) => {
      element.dataset.motionReveal = "";
      if (element.tagName === "ARTICLE") element.dataset.motionCard = "";
    });

    if (effectivePreset === "none" || !("IntersectionObserver" in window)) {
      revealTargets.forEach((element) => element.classList.add("motion-visible"));
      return () => {
        revealTargets.forEach((element) => element.classList.remove("motion-visible"));
        delete main.dataset.motionReady;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("motion-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8%", threshold: 0.08 },
    );
    revealTargets.forEach((element) => observer.observe(element));

    const depthTargets = Array.from(main.querySelectorAll<HTMLElement>("[data-motion-depth]"));
    let animationFrame = 0;
    const updateDepth = () => {
      animationFrame = 0;
      if (!window.matchMedia("(min-width: 768px)").matches) {
        depthTargets.forEach((element) => element.style.removeProperty("--motion-depth-y"));
        return;
      }
      const range = effectivePreset === "immersive" ? 48 : 18;
      depthTargets.forEach((element) => {
        const rect = element.parentElement?.getBoundingClientRect();
        if (!rect || rect.bottom < 0 || rect.top > window.innerHeight) return;
        const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        const offset = Math.max(-range, Math.min(range, progress * -range));
        element.style.setProperty("--motion-depth-y", `${offset.toFixed(1)}px`);
      });
    };
    const queueDepthUpdate = () => {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(updateDepth);
    };
    updateDepth();
    window.addEventListener("scroll", queueDepthUpdate, { passive: true });
    window.addEventListener("resize", queueDepthUpdate);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", queueDepthUpdate);
      window.removeEventListener("resize", queueDepthUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      revealTargets.forEach((element) => element.classList.remove("motion-visible"));
      depthTargets.forEach((element) => element.style.removeProperty("--motion-depth-y"));
      delete main.dataset.motionReady;
    };
  }, [defaultPreset, homepageModule, modulePresets, pathname, reducedMotion]);

  return null;
}
